import { FC, useEffect, useState, useRef, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { Skeleton } from '@/components/ui/skeleton';
import { useProfile } from '@/hooks/useProfile';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { DigitalMaterial } from '@/types/library';
import { useToast } from '@/components/ui/use-toast';
import DOMPurify from 'dompurify';

// Componente memo para el contenido del artículo
const ArticleContent = memo(({ content }: { content: string }) => {
  const sanitizedContent = DOMPurify.sanitize(content);
  return <div className="mt-8" dangerouslySetInnerHTML={{ __html: sanitizedContent }} />;
});

ArticleContent.displayName = 'ArticleContent';

// Componente memo para la imagen de portada
const CoverImage = memo(({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, []);

  return (
    <div className="mt-8 relative min-h-[400px]">
      {!isLoaded && (
        <Skeleton className="absolute inset-0 w-full h-[400px] rounded-xl" />
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`w-full h-[400px] object-cover rounded-xl ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setIsLoaded(true)}
        loading="eager"
      />
    </div>
  );
});

CoverImage.displayName = 'CoverImage';

const MaterialReader: FC = () => {
  console.log('MaterialReader: Iniciando renderizado');
  
  const { materialId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userProfile } = useProfile();
  const { getMaterial } = useDigitalMaterials();
  const [material, setMaterial] = useState<DigitalMaterial | null>(null);
  const [displayMaterial, setDisplayMaterial] = useState<DigitalMaterial | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log('MaterialReader: useEffect iniciado, materialId:', materialId);
    
    const loadMaterial = async () => {
      if (!materialId) {
        console.log('MaterialReader: No hay materialId');
        return;
      }

      console.log('MaterialReader: Iniciando carga del material');
      setLoading(true);
      setError(null);

      try {
        console.log('MaterialReader: Llamando a getMaterial');
        const materialData = await getMaterial(materialId);
        console.log('MaterialReader: Material recibido:', materialData);
        
        setMaterial(materialData);
        setDisplayMaterial(materialData);
        console.log('MaterialReader: Material establecido en el estado');
      } catch (err) {
        console.error('MaterialReader: Error cargando material:', err);
        setError(err as Error);
        
        if ((err as Error).message.includes('No hay sesión activa')) {
          toast({
            variant: "destructive",
            title: "Sesión expirada",
            description: "Por favor, inicia sesión nuevamente para continuar.",
          });
          navigate('/login');
          return;
        }
      } finally {
        console.log('MaterialReader: Finalizando carga');
        setLoading(false);
      }
    };

    loadMaterial();
  }, [materialId, getMaterial, navigate, toast]);

  console.log('MaterialReader: Estado actual:', { loading, error, material });

  if (loading && !displayMaterial) {
    console.log('MaterialReader: Renderizando skeleton');
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <Button variant="ghost" disabled className="opacity-50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a la Biblioteca
            </Button>
            <div className="space-y-6">
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-[400px] w-full rounded-xl" />
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !displayMaterial) {
    console.log('MaterialReader: Renderizando error');
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error al cargar el material</AlertTitle>
            <AlertDescription>
              {error?.message || 'No pudimos encontrar el material que estás buscando.'}
            </AlertDescription>
          </Alert>
          <Button
            onClick={() => navigate('/library')}
            className="mt-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a la Biblioteca
          </Button>
        </div>
      </div>
    );
  }

  if (material.isPremium && (!userProfile?.isPremium || !userProfile?.isActive)) {
    console.log('MaterialReader: Renderizando bloqueo premium');
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              className="mb-6"
              onClick={() => navigate('/library')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a la Biblioteca
            </Button>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Contenido Premium</AlertTitle>
              <AlertDescription>
                Este material es exclusivo para usuarios Premium.
              </AlertDescription>
            </Alert>

            <Button
              onClick={() => navigate('/pricing')}
              className="mt-6"
              variant="premium"
            >
              Actualizar a Premium
            </Button>
          </div>
        </div>
      </div>
    );
  }

  console.log('MaterialReader: Renderizando contenido');

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/library')}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a la Biblioteca
          </Button>

          <article className="prose prose-lg max-w-none">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {displayMaterial?.title || material?.title}
            </h1>
            
            <CoverImage src={displayMaterial?.coverImage || material?.coverImage} alt={displayMaterial?.title || material?.title} />
            
            <ArticleContent content={displayMaterial?.content || material?.content} />
          </article>

          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            {(displayMaterial?.type === 'PDF' || material?.type === 'PDF') && (displayMaterial?.fileUrl || material?.fileUrl) && (
              <Button
                onClick={() => window.open(displayMaterial?.fileUrl || material?.fileUrl, '_blank')}
                className="w-full sm:w-auto"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            )}

            <Button
              variant="ghost"
              onClick={() => navigate('/library')}
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a la Biblioteca
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialReader;
